import type { MermaidConfig } from 'mermaid';
import { useContext, useEffect, useRef, useState } from 'react';
// import { useDeferredRender } from "../../hooks/use-deferred-render";
import { PanZoom } from './pan-zoom';
import { initializeMermaid } from './utils';
import { StreamdownContext } from 'streamdown';
import { useDeferredRender } from '@/embedded/chat/hooks/use-defered-render';
import { cn } from '@/utils/cn';
import { useT } from '@/utils/i18n';

type MermaidProps = {
  chart: string;
  className?: string;
  config?: MermaidConfig;
  fullscreen?: boolean;
  showControls?: boolean;
};

export const Mermaid = ({
  chart,
  className,
  config,
  fullscreen = false,
  showControls = true,
}: MermaidProps) => {
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [svgContent, setSvgContent] = useState<string>('');
  const [lastValidSvg, setLastValidSvg] = useState<string>('');
  const svgContentRef = useRef('');
  const lastValidSvgRef = useRef('');
  const [retryCount, setRetryCount] = useState(0);
  const { mermaid: mermaidContext } = useContext(StreamdownContext);
  const ErrorComponent = mermaidContext?.errorComponent;

  // Use deferred render hook for optimal performance
  const { shouldRender, containerRef } = useDeferredRender({
    immediate: fullscreen,
  });

  useEffect(() => {
    svgContentRef.current = svgContent;
  }, [svgContent]);

  useEffect(() => {
    lastValidSvgRef.current = lastValidSvg;
  }, [lastValidSvg]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: "Required for Mermaid"
  useEffect(() => {
    // Only render when shouldRender is true
    if (!shouldRender) {
      return;
    }

    const renderChart = async () => {
      try {
        setError(null);
        setIsLoading(true);

        // Initialize mermaid with optional custom config
        const mermaid = await initializeMermaid(config);

        // Use a stable ID based on chart content hash and timestamp to ensure uniqueness
        const chartHash = chart.split('').reduce((acc, char) => {
          // biome-ignore lint/suspicious/noBitwiseOperators: "Required for Mermaid"
          return ((acc << 5) - acc + char.charCodeAt(0)) | 0;
        }, 0);
        const uniqueId = `mermaid-${Math.abs(chartHash)}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

        const { svg } = await mermaid.render(uniqueId, chart);

        // Update both current and last valid SVG
        setSvgContent(svg);
        setLastValidSvg(svg);
      } catch (err) {
        // Silently fail and keep the last valid SVG
        // Don't update svgContent here - just keep what we have

        // Only set error if we don't have any valid SVG
        if (!(lastValidSvgRef.current || svgContentRef.current)) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : t('Failed to render Mermaid chart');
          setError(errorMessage);
        }
      } finally {
        setIsLoading(false);
      }
    };

    renderChart();
  }, [chart, config, retryCount, shouldRender, t]);

  // Show placeholder when not scheduled to render
  if (!(shouldRender || svgContent || lastValidSvg)) {
    return (
      <div
        className={cn('my-4 min-h-[12.5rem]', className)}
        ref={containerRef}
      />
    );
  }

  if (isLoading && !svgContent && !lastValidSvg) {
    return (
      <div
        className={cn('my-4 flex justify-center p-4', className)}
        ref={containerRef}
      >
        <div className="flex items-center space-x-2 text-muted-foreground">
          <div className="h-4 w-4 animate-spin rounded-full border-current border-b-2" />
          <span className="text-sm">{t('Loading diagram...')}</span>
        </div>
      </div>
    );
  }

  // Only show error if we have no valid SVG to display
  if (error && !svgContent && !lastValidSvg) {
    const retry = () => setRetryCount(count => count + 1);

    // Use custom error component if provided
    if (ErrorComponent) {
      return (
        <div ref={containerRef}>
          <ErrorComponent chart={chart} error={error} retry={retry} />
        </div>
      );
    }

    // Default error display
    return (
      <div className={cn('p-4', className)} ref={containerRef}>
        <p className="font-mono text-apple-red text-sm">
          {t('Mermaid Error')}: {error}
        </p>
        <details className="mt-2">
          <summary className="cursor-pointer text-apple-red text-xs">
            {t('Show Code')}
          </summary>
          <pre className="mt-2 overflow-x-auto rounded-xl bg-black/10 dark:bg-white/10 p-2 text-apple-red text-xs">
            {chart}
          </pre>
        </details>
      </div>
    );
  }

  // Always render the SVG if we have content (either current or last valid)
  const displaySvg = svgContent || lastValidSvg;

  return (
    <div className="size-full" ref={containerRef}>
      <PanZoom
        className={cn(
          fullscreen ? 'size-full overflow-hidden' : 'my-4 overflow-hidden',
          className
        )}
        fullscreen={fullscreen}
        maxZoom={3}
        minZoom={0.5}
        showControls={showControls}
        zoomStep={0.1}
      >
        <div
          aria-label={t('Mermaid chart')}
          className={cn(
            'flex justify-center',
            fullscreen ? 'size-full items-center' : null
          )}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: "Required for Mermaid"
          dangerouslySetInnerHTML={{ __html: displaySvg }}
          role="img"
        />
      </PanZoom>
    </div>
  );
};
